import { t } from "i18next";
import { useEffect, useState } from "react";

import { Button } from "@src/components/ui-components/Buttons/Button/Button";
import { Checkbox } from "@src/components/ui-components/Checkbox/Checkbox";
import { apiSettingsQueryKey, setCompanyGeneralSettings, useRealtimeCompanyGeneralSettings } from "@src/data-hooks/general-settings/settings.service";
import { getCurrentUser } from "@src/auth/getCurrentUser";

import { queryClient } from "@src/index";
import { useModulesStatus } from "@src/auth/useModulesStatus";
import { isFieldEnabled } from "@src/utils/settings/settings.utils";
import { Icon } from "../../../../../components/deprecated/Icon/Icon";
import { Text } from "../../../../../components/deprecated/Text/Text";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { CompanyData } from "../../../../../to-be-removed/LocalData/Company/CompanyData";
import { PropertyInfoFieldGroups } from "../../../../../to-be-removed/LocalData/General/DataObjectHelpers/Property/InfoFields/PropertyInfoFieldGroups";
import { UnitInfoFieldGroups } from "../../../../../to-be-removed/LocalData/General/DataObjectHelpers/Property/InfoFields/UnitInfoFieldGroups";
import { DataObjectHelpers } from "../../../../../to-be-removed/LocalData/General/DataObjects/DataObjectHelpers";
import styles from "./PropertyFieldsSettings.module.css";

interface PropertyFieldsSettingProps {
  fieldInformation: "property" | "unit";
  company: CompanyData;
}

interface FieldData {
  [key: string]: {
    enabled: boolean;
    category: string;
  };
}
type CategoryType = { category: string; label: string };

export const PropertyFieldsSetting = (props: PropertyFieldsSettingProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { userId } = getCurrentUser();
  const { data: settings } = useRealtimeCompanyGeneralSettings(props.company.id);
  const { tenantEconomy: financeModuleEnabled } = useModulesStatus();

  const propertyCategories: CategoryType[] = [
    { category: "general", label: t("main.generalInformation") },
    ...(props.company.properties.some((property) => property.moduleIsActivated("tenantEconomy"))
      ? [{ category: "finance", label: t("main.finance") }]
      : []),
    { category: "note", label: t("main.note") },
  ];

  const tenantCategories: CategoryType[] = [
    { category: "general", label: t("main.generalInformation") },
    ...(props.company.properties.some((property) => property.moduleIsActivated("tenantEconomy"))
      ? [{ category: "finance", label: t("main.finance") }]
      : []),
    { category: "landlordAdmin", label: t("main.landlordAdmin") },
    { category: "note", label: t("main.note") },
  ];

  const [fields, setFields] = useState<FieldData>({});
  const [changedFields, setChangedFields] = useState<FieldData>({});
  const [loading, setLoading] = useState(true);
  const categories = props.fieldInformation === "property" ? propertyCategories : tenantCategories;
  const helper = props.fieldInformation === "property" ? DataObjectHelpers.property : DataObjectHelpers.unit;

  useEffect(() => {
    const settingsFields = props.fieldInformation === "property" ? settings?.propertyFields : settings?.unitFields;
    const fieldGroups = props.fieldInformation === "property" ? PropertyInfoFieldGroups : UnitInfoFieldGroups;

    const fields: FieldData = {};
    for (const category of Object.keys(fieldGroups)) {
      for (const fieldID of Object.keys(fieldGroups[category].fields)) {
        if (settingsFields?.[fieldID]) {
          fields[fieldID] = {
            enabled: settingsFields[fieldID].enabled,
            category: category,
          };
        } else {
          fields[fieldID] = {
            enabled: isFieldEnabled(fieldID, props.fieldInformation, financeModuleEnabled, settings),
            category: category,
          };
        }
      }
    }
    setFields(fields);
    setChangedFields({});
    setLoading(false);
  }, [settings, props.fieldInformation]);

  const updateCheckedField = (key: string, checkedStatus: boolean, category: string) => {
    let updatedFields: FieldData = { ...fields };
    updatedFields[key] = { enabled: checkedStatus, category: category };
    setFields(updatedFields);
    setChangedFields({ ...changedFields, [key]: { enabled: checkedStatus, category: category } });
  };

  const saveFieldSettings = () => {
    const saveKey = props.fieldInformation === "property" ? "propertyFields" : "unitFields";
    setIsSaving(true);
    const promise = setCompanyGeneralSettings(props.company.id, userId, {
      [saveKey]: changedFields,
    })
      .then(() => {
        queryClient.invalidateQueries(apiSettingsQueryKey());
      })
      .finally(() => {
        setIsSaving(false);
      });

    ToastUtilities.presentSavingToast(promise);
  };

  return (
    <div id={styles.rightContent}>
      <Text
        size="xl"
        isBold
        text={props.fieldInformation === "property" ? t("main.propertyInformation") : t("main.tenantInformation")}
        className={styles.rightTitle}
      />
      <div>
        {categories.map((category) => {
          return (
            <div key={category.label}>
              <Text size="md" text={category.label + ":"} isItalic marginTop={32} marginBottom={4} />
              <div className={styles.fieldContainer}>
                <ul>
                  {Object.keys(fields)
                    .filter((key) => fields[key].category === category.category)
                    .sort(
                      (a, b) => helper.allFields().findIndex((field) => field.key === a) - helper.allFields().findIndex((field) => field.key === b)
                    )
                    .map((key) => {
                      const field = fields[key];
                      return (
                        <li className={styles.fieldCheckboxListItem} key={key}>
                          <div className={styles.fieldBox}>
                            <Checkbox
                              className={styles.fieldCheckbox}
                              description={helper.getField(key)?.title}
                              value={field.enabled}
                              disabled={key === "name" || loading || isSaving ? true : false}
                              onChange={(checkedStatus) => {
                                updateCheckedField(key, checkedStatus, field.category);
                              }}
                            />
                          </div>
                        </li>
                      );
                    })}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
      <Button
        title={t("main.save") + " " + Object.keys(changedFields).length + " " + t("main.changes").toLowerCase()}
        leadingIcon={<Icon size={20} marginRight={5} name="save" color="white" />}
        state={loading || isSaving ? "loading" : Object.keys(changedFields).length > 0 ? "default" : "disabled"}
        marginTop={35}
        onClick={() => saveFieldSettings()}
      />
    </div>
  );
};
